import { forwardRef, type InputHTMLAttributes } from 'react';
import { clsx } from 'clsx';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
  icon?: React.ReactNode;
  rightElement?: React.ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ 
    label,
    error,
    hint,
    icon,
    rightElement,
    className,
    id,
    ...props 
  }, ref) => {
    const inputId = id || `input-${Math.random().toString(36).substring(7)}`;
    
    return (
      <div className="w-full">
        {label && (
          <label 
            htmlFor={inputId} 
            className="block text-sm font-medium text-swiss-charcoal mb-1"
          >
            {label}
            {props.required && <span className="text-swiss-crimson ml-1">*</span>}
          </label>
        )}
        
        <div className="relative">
          {icon && (
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <span className="text-swiss-stone text-sm">{icon}</span>
            </div>
          )}
          
          <input
            ref={ref}
            id={inputId}
            className={clsx(
              'w-full px-3 py-2 text-sm border rounded-swiss transition-colors',
              'focus:outline-none focus:ring-2 focus:ring-brand-primary focus:ring-offset-1',
              'placeholder:text-swiss-stone',
              'disabled:bg-swiss-pearl disabled:cursor-not-allowed',
              {
                'pl-10': icon,
                'pr-10': rightElement,
                'border-swiss-crimson focus:border-swiss-crimson': error,
                'border-swiss-mist focus:border-brand-primary': !error,
              },
              className
            )}
            aria-invalid={!!error}
            aria-describedby={error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined}
            {...props}
          />
          
          {rightElement && (
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
              {rightElement}
            </div>
          )}
        </div>
        
        {error && (
          <p id={`${inputId}-error`} className="mt-1 text-xs text-swiss-crimson">
            {error}
          </p>
        )}
        
        {hint && !error && (
          <p id={`${inputId}-hint`} className="mt-1 text-xs text-swiss-stone">
            {hint}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';